// =============================================================================
// features/relatorios/comercial/api.ts — Funções de API da aba Comercial de
// /relatorios (F35-S12).
//
// Consome GET /api/sales/reports/overview e POST /api/sales/reports/export.
// Tipos vêm de @elemento/shared-schemas — sem drift front×API.
// LGPD (doc 17 §3.3): responses são apenas agregados (sem PII de titular).
// =============================================================================
import type {
  SalesReportsExportRequest,
  SalesReportsOverviewResponse,
  SalesReportsQuery,
} from '@elemento/shared-schemas';
import { SalesReportsOverviewResponseSchema } from '@elemento/shared-schemas';

import { api } from '../../../lib/api';
import { useAuthStore } from '../../../lib/auth-store';

function buildParams(query: Partial<SalesReportsQuery>): URLSearchParams {
  const params = new URLSearchParams();

  if (query.range) params.set('range', query.range);
  if (query.dateFrom) params.set('dateFrom', query.dateFrom);
  if (query.dateTo) params.set('dateTo', query.dateTo);
  if (query.cityIds?.length) {
    for (const id of query.cityIds) params.append('cityIds', id);
  }
  if (query.agentId) params.set('agentId', query.agentId);
  if (query.campaignId) params.set('campaignId', query.campaignId);

  return params;
}

export async function fetchSalesReportsOverview(
  query: Partial<SalesReportsQuery>,
): Promise<SalesReportsOverviewResponse> {
  const params = buildParams(query);
  const raw = await api.get<unknown>(`/api/sales/reports/overview?${params.toString()}`);
  return SalesReportsOverviewResponseSchema.parse(raw);
}

// ---------------------------------------------------------------------------
// POST /api/sales/reports/export — retorna Blob para download (mesma técnica
// de `../api.ts#postReportsExport`, CSV puro nesta camada).
// ---------------------------------------------------------------------------

const API_BASE = (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:3333';

function getCsrfToken(): string {
  const match = document.cookie.split('; ').find((row) => row.startsWith('csrf_token='));
  return match ? (match.split('=')[1] ?? '') : '';
}

export interface SalesReportsExportResult {
  blob: Blob;
  filename: string;
}

export async function postSalesReportsExport(
  request: SalesReportsExportRequest,
): Promise<SalesReportsExportResult> {
  const accessToken = useAuthStore.getState().accessToken;
  const csrf = getCsrfToken();

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  if (csrf) headers['X-CSRF-Token'] = csrf;

  const res = await fetch(`${API_BASE}/api/sales/reports/export`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(request),
  });

  if (!res.ok) {
    let message = `Erro ao exportar (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string; error?: string };
      if (body.message) message = body.message;
      else if (body.error) message = body.error;
    } catch {
      // body não era JSON
    }
    throw new Error(message);
  }

  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
  const filename = filenameMatch?.[1] ?? 'relatorio-comercial.csv';

  return { blob, filename };
}
